import {
    IWatchedItem,
    IWatcher,
    Ulid,
    UserSettingsGetResponse,
} from "@kase/my-marathon-communication";
import { signal } from "@preact/signals";
import { ApiSliceParams } from "../interfaces";
import { createEmitter } from "../utils";
import {
    CustomEntryPrio,
    SetUserSettingsRequest,
    UserSettings,
} from "./interfaces";
import { customEntryPrioFromApi, customEntryPrioToApi } from "./utils";

const DEFAULT_CUSTOM_PRIO = [
    CustomEntryPrio.Comment,
    CustomEntryPrio.ClientFeeCode,
    CustomEntryPrio.Client,
    CustomEntryPrio.FeeCode,
];
const DEFAULT_USER_SETTINGS = {
    _id: Ulid.ZERO,
    id: Ulid.ZERO.toString(),
    time: {
        _custom_entry_prio: customEntryPrioToApi(DEFAULT_CUSTOM_PRIO),
        customEntryPrio: DEFAULT_CUSTOM_PRIO,
        suggest_description: false,
        suggest_fee_code: false,
        suggest_project: false,
        suggest_lob: false,
        use_start: true,
    },
    expense: {
        ocr_category_as_description: false,
    },
};

const getETag = (item: UserSettings) =>
    [
        item.time._custom_entry_prio,
        item.time.suggest_description,
        item.time.suggest_fee_code,
        item.time.suggest_project,
        item.time.suggest_lob,
        item.time.use_start,
        item.expense.ocr_category_as_description,
    ].join("|");

export const makeUser = ({ connection }: ApiSliceParams) => {
    const loading = signal(false);
    const loaded = signal(false);
    const store = signal<UserSettings>(DEFAULT_USER_SETTINGS);
    const { emit, on, off } = createEmitter<UserSettings>();
    let watcher: IWatcher<IWatchedItem<Ulid, UserSettingsGetResponse>> | null =
        null;

    const set = async ({ time, expense }: SetUserSettingsRequest) => {
        const con = connection.value;
        const old = store.peek();

        await con.usersettings.set({
            time: time
                ? {
                      custom_entry_prio: time.customEntryPrio
                          ? customEntryPrioToApi(time.customEntryPrio)
                          : old.time._custom_entry_prio,
                      suggest_description:
                          time.suggest_description ??
                          old.time.suggest_description,
                      suggest_fee_code:
                          time.suggest_fee_code ?? old.time.suggest_fee_code,
                      suggest_project:
                          time.suggest_project ?? old.time.suggest_project,
                      suggest_lob: time.suggest_lob ?? old.time.suggest_lob,
                      use_start: time.use_start ?? old.time.use_start,
                  }
                : undefined,
            expense: expense
                ? {
                      ocr_category_as_description:
                          expense.ocr_category_as_description ??
                          old.expense.ocr_category_as_description,
                  }
                : undefined,
        });
    };

    const listen = () => {
        if (watcher) {
            return;
        }

        const con = connection.value;

        loading.value = true;

        watcher = con.usersettings.get();

        watcher.listen((value) => {
            const result = value.item;
            const old = store.peek();

            if (result) {
                const newValue = {
                    id: result.id.toString(),
                    _id: result.id,
                    time: {
                        _custom_entry_prio:
                            result.time?.custom_entry_prio ??
                            old.time._custom_entry_prio,
                        customEntryPrio: result.time?.custom_entry_prio
                            ? customEntryPrioFromApi(
                                  result.time.custom_entry_prio,
                              )
                            : old.time.customEntryPrio,
                        suggest_description:
                            result.time?.suggest_description ??
                            old.time.suggest_description,
                        suggest_fee_code:
                            result.time?.suggest_fee_code ??
                            old.time.suggest_fee_code,
                        suggest_project:
                            result.time?.suggest_project ??
                            old.time.suggest_project,
                        suggest_lob:
                            result.time?.suggest_lob ?? old.time.suggest_lob,
                        use_start: result.time?.use_start ?? old.time.use_start,
                    },
                    expense: {
                        ocr_category_as_description:
                            result.expense?.ocr_category_as_description ??
                            old.expense.ocr_category_as_description,
                    },
                };

                store.value = newValue;
            } else {
                store.value = DEFAULT_USER_SETTINGS;
            }

            if (!loaded.peek()) {
                loaded.value = true;
                loading.value = false;
            }

            if (getETag(old) !== getETag(store.peek())) {
                emit("add", [store.peek()]);
            }

            return true;
        });
    };

    return {
        store,
        loading,
        loaded,
        listen,
        set,
        on,
        off,
    };
};
